<script lang="ts">
  import { onMount } from 'svelte'
  import { api } from '$lib/api/client'
  import type { paths } from '$lib/api/generated'
  import { showApiError } from '$lib/util/errors'
  import { pushToast } from '$lib/util/toasts'
  import { session } from '$lib/auth/session'
  import FormField from '../../components/FormField.svelte'

  type UserItem =
    paths['/users']['get']['responses'][200]['content']['application/json']['items'][number]
  type Role = UserItem['role']

  const ROLES: Role[] = ['VIEWER', 'REVIEWER', 'MANAGER', 'ADMIN']

  let items = $state<UserItem[]>([])
  let loading = $state(false)

  let email = $state('')
  let role = $state<Role>('VIEWER')
  let creating = $state(false)

  async function load(): Promise<void> {
    loading = true
    const { data, error } = await api.GET('/users', {})
    if (error) {
      showApiError(error)
      loading = false
      return
    }
    items = data?.items ?? []
    loading = false
  }

  async function handleCreate(e: SubmitEvent): Promise<void> {
    e.preventDefault()
    creating = true
    const { error } = await api.POST('/users', { body: { email, role } })
    creating = false
    if (error) {
      showApiError(error)
      return
    }
    pushToast('success', `Invite email sent to ${email}.`)
    email = ''
    role = 'VIEWER'
    await load()
  }

  async function handleRoleChange(id: number, newRole: Role): Promise<void> {
    const { error } = await api.PATCH('/users/{id}', {
      params: { path: { id } },
      body: { role: newRole },
    })
    if (error) showApiError(error)
    await load()
  }

  async function deactivate(id: number): Promise<void> {
    const { error } = await api.POST('/users/{id}/deactivate', { params: { path: { id } } })
    if (error) {
      showApiError(error)
      return
    }
    await load()
  }

  async function reactivate(id: number): Promise<void> {
    const { error } = await api.POST('/users/{id}/reactivate', { params: { path: { id } } })
    if (error) {
      showApiError(error)
      return
    }
    await load()
  }

  async function resendInvite(id: number): Promise<void> {
    const { error } = await api.POST('/users/{id}/resend-invite', { params: { path: { id } } })
    if (error) {
      showApiError(error)
      return
    }
    pushToast('success', 'Invite email sent.')
    await load()
  }

  async function sendReset(id: number): Promise<void> {
    const { error } = await api.POST('/users/{id}/send-reset', { params: { path: { id } } })
    if (error) {
      showApiError(error)
      return
    }
    pushToast('success', 'Reset email sent.')
    await load()
  }

  function chipClass(status: UserItem['status']): string {
    if (status === 'ACTIVE') return 'chip ok'
    if (status === 'DEACTIVATED') return 'chip bad'
    return 'chip warn'
  }

  function statusLabel(status: UserItem['status']): string {
    if (status === 'INVITED') return 'Invited'
    if (status === 'ACTIVE') return 'Active'
    return 'Deactivated'
  }

  onMount(() => {
    void load()
  })
</script>

<div class="section-header">
  <h1>Users</h1>
</div>

<form class="create-form" onsubmit={handleCreate}>
  <FormField label="Email" name="email" type="email" bind:value={email} required />
  <div class="form-field">
    <label for="role">Role</label>
    <select id="role" bind:value={role}>
      {#each ROLES as r (r)}
        <option value={r}>{r}</option>
      {/each}
    </select>
  </div>
  <button type="submit" class="primary" disabled={creating || !email.trim()}>
    {creating ? 'Creating…' : 'Create user'}
  </button>
</form>

{#if items.length === 0}
  <p class="muted">{loading ? 'Loading…' : 'No users yet.'}</p>
{:else}
  <table>
    <thead>
      <tr>
        <th>Email</th>
        <th>Role</th>
        <th>Status</th>
        <th>Created</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      {#each items as u (u.id)}
        {@const isSelf = u.id === $session.user?.id}
        <tr>
          <td>{u.email}</td>
          <td>
            {#if isSelf}
              {u.role}
            {:else}
              <select
                value={u.role}
                onchange={(e) =>
                  void handleRoleChange(u.id, (e.currentTarget as HTMLSelectElement).value as Role)}
              >
                {#each ROLES as r (r)}
                  <option value={r}>{r}</option>
                {/each}
              </select>
            {/if}
          </td>
          <td><span class={chipClass(u.status)}>{statusLabel(u.status)}</span></td>
          <td>{new Date(u.createdAt).toLocaleDateString()}</td>
          <td class="actions">
            {#if u.status === 'DEACTIVATED'}
              <button type="button" onclick={() => void reactivate(u.id)}>Reactivate</button>
            {:else if !isSelf}
              <button type="button" onclick={() => void deactivate(u.id)}>Deactivate</button>
            {/if}
            {#if u.status === 'INVITED'}
              <button type="button" onclick={() => void resendInvite(u.id)}>Resend invite</button>
            {/if}
            {#if u.status === 'ACTIVE' || u.status === 'DEACTIVATED'}
              <button type="button" onclick={() => void sendReset(u.id)}>Send reset</button>
            {/if}
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
{/if}

<style>
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }
  h1 {
    margin: 0;
    font-size: 1.75rem;
  }
  .muted {
    color: var(--muted);
  }
  .create-form {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    margin-bottom: 24px;
    max-width: 600px;
  }
  .create-form :global(.form-field) {
    flex: 1;
    margin-bottom: 0;
  }
  .form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  }
  .form-field label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--ink);
  }
  select {
    padding: 10px 12px;
    min-height: 44px;
    box-sizing: border-box;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    font-size: 1rem;
    background: var(--surface-2);
    color: var(--ink);
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }
  th,
  td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid var(--line);
    vertical-align: middle;
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .chip {
    font-size: 0.72rem;
    font-weight: 600;
    padding: 1px 7px;
    border-radius: 999px;
    background: var(--surface-3);
    color: var(--muted);
  }
  .chip.ok {
    background: var(--ok-bg);
    color: var(--ok-ink);
  }
  .chip.bad {
    background: var(--err-bg);
    color: var(--err-ink);
  }
  .chip.warn {
    background: color-mix(in srgb, var(--gold) 24%, var(--surface-2));
    color: var(--ink-strong);
  }
  button.primary {
    background: var(--brand);
    color: white;
    padding: 6px 14px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
  }
  button.primary:hover:not(:disabled) {
    background: var(--brand-hover);
  }
  button.primary:disabled {
    background: color-mix(in srgb, var(--brand) 40%, var(--surface-2));
    cursor: not-allowed;
  }
  .actions button {
    padding: 4px 10px;
    background: var(--surface-2);
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8rem;
  }
  .actions button:hover {
    background: var(--surface-3);
  }
</style>
